import { DollarOutlined } from "@ant-design/icons"
import { Card, Divider, Empty, Flex, Skeleton, Spin, Typography } from "antd"
import { useCallback, useEffect, useState } from "react"
import { useParams } from "react-router"

import { useAppDispatch } from "../../../../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../../../../hooks/use-app-selector.ts"
import { budgetFormat } from "../../../../../../utils/number/budget-format.ts"
import { fetchInvoiceSnapshot } from "../../../../redux/project-invoices/project-invoices.thunks.ts"
import { FETCHING_SNAPSHOT_ENUM } from "../../../../redux/project.types.ts"

const { Title, Text } = Typography

export function ProjectViewInvoiceSnapshot() {
  const projectId = useParams()?.id as string
  const dispatch = useAppDispatch()

  const [initialized, setInitialized] = useState(false)
  const data = useAppSelector((state) => state?.project?.invoiceSnapshot)

  const fetching = useAppSelector(
    (state) =>
      state?.project?.fetchingSnapshotMap?.[FETCHING_SNAPSHOT_ENUM.INVOICE]
  )

  const handleFetchInvoiceSnapshot = useCallback(async () => {
    await dispatch(fetchInvoiceSnapshot(projectId))
    setInitialized(true)
  }, [dispatch, projectId])

  useEffect(() => {
    void handleFetchInvoiceSnapshot()
  }, [handleFetchInvoiceSnapshot])

  if (fetching && !initialized) {
    return (
      <Card title={<Title level={5}>Invoice Snapshot</Title>} size="small">
        <Skeleton.Node active className="w-full h-40" />
      </Card>
    )
  }

  return (
    <Spin spinning={fetching} size="large">
      <Card title={<Title level={5}>Invoice Snapshot</Title>} size="small">
        {data ? (
          <Flex vertical gap={12}>
            <Flex
              vertical
              gap={4}
              flex={1}
              className={"bg-primary/5 p-3 rounded-card"}
              data-testid="invoice-snapshot-outstanding"
            >
              <Flex justify={"space-between"}>
                <Flex vertical gap={2}>
                  <Text type="secondary">Total Outstanding</Text>
                  <Text strong className="text-2xl">
                    {budgetFormat(data?.total_outstanding)}
                  </Text>
                </Flex>
                <div className="size-12 rounded-lg bg-orange-100 flex items-center justify-center">
                  <DollarOutlined className="text-xl text-orange-500" />
                </div>
              </Flex>

              <Flex gap="large" className="mt-2" justify={"space-between"}>
                <Flex vertical gap={2}>
                  <Text type="secondary">Pending</Text>
                  <Text strong>{budgetFormat(data?.pending)}</Text>
                </Flex>

                <Flex vertical gap={2}>
                  <Text type="secondary">Overdue</Text>
                  <Text strong type="danger">
                    {budgetFormat(data?.overdue)}
                  </Text>
                </Flex>
              </Flex>
            </Flex>

            <Divider className="my-0" />

            <Flex vertical gap={2} data-testid="invoice-snapshot-paid">
              <Flex justify={"space-between"} align={"center"}>
                <Text type="secondary">Paid</Text>
                <Text strong>{budgetFormat(data?.paid)}</Text>
              </Flex>
              <Text type="secondary" className="text-xs">
                Total billed and settled — separate from the outstanding figures
                above
              </Text>
            </Flex>
          </Flex>
        ) : (
          <Empty description="No invoices available" />
        )}
      </Card>
    </Spin>
  )
}
